import React, { Fragment } from 'react';

import { cn } from '@/lib/utils';

import Card from '@/components/atoms/card/index';
import LinkButton from '@/components/atoms/linkButton';
import { Text } from '@/components/atoms/text';
import { OfferCountdownStatus } from '@/components/countdown';

import IconSupport from '~/svg/IconSupport.svg';
import { LinkButtonSBProps } from '@/components/atoms/linkButton/LinkSB';

export type BannerProps = {
  title: string;
  description: React.ReactNode;
  direction: 'column' | 'row' | null;
  image: React.ReactNode;
  countdownExpiry?: string | null;
  items: LinkButtonSBProps[];
  classNames?: {
    root?: string;
  };
};

export default function Banner({
  items,
  image,
  title,
  description,
  direction,
  classNames,
  countdownExpiry,
}: BannerProps) {
  const hasCountdown = !!countdownExpiry;

  return (
    <Card
      classNames={{
        root: cn(
          'flex flex-col gap-5 lg:gap-6 dark:bg-transparent bg-transparent px-5 lg:px-6 py-8 lg:py-9',
          classNames?.root
        ),
      }}
      color='gradient'
    >
      <div className='flex items-center gap-4'>
        <div
          className={cn(
            'flex flex-grow items-center gap-x-3 text-left',
            hasCountdown && 'gap-5 lg:gap-x-6 lg:gap-y-6'
          )}
        >
          {!!image && image}
          <div className='space-y-3'>
            <Text variant='subtitle-l' color='neutral-N99'>
              {title}
            </Text>
            {!!description && <Text color='neutral-N80'>{description}</Text>}
          </div>
        </div>
        <div className={cn('hidden', direction === 'row' && 'lg:block')}>
          <BannerActions items={items} />
        </div>
      </div>

      {hasCountdown && (
        <div className='bg-neutral-N04 mt-3 flex justify-center rounded-xl p-4'>
          <OfferCountdownStatus expiry={countdownExpiry} />
        </div>
      )}

      <div className={cn(direction === 'row' && 'lg:hidden')}>
        <BannerActions items={items} />
      </div>
    </Card>
  );
}

export function BannerActions({ items }: { items: BannerProps['items'] }) {
  return items.map((item, index) =>
    1 === index ? (
      <Fragment key={index}>
        <p className='text-neutral-N50 my-3 flex justify-center'>or</p>
        <LinkButton
          href={item.url}
          variant={item?.variant ?? 'outlined'}
          color={item?.color ?? 'secondary'}
          fullwidth={true}
          leftSection={<IconSupport />}
        >
          {item.text}
        </LinkButton>
      </Fragment>
    ) : (
      <LinkButton
        key={index}
        href={item.url}
        variant={item?.variant ?? 'filled'}
        color={item?.color ?? 'secondary'}
        fullwidth={true}
      >
        {item.text}
      </LinkButton>
    )
  );
}
